import React, { useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowBack as ArrowBackIcon } from "@material-ui/icons";
import { Box, Grid, MenuItem, Typography } from "@material-ui/core";
import useStyles from "../paymentStyles";
import { FormattedMessage } from "react-intl";
import translate from "../../../../../../i18n/translate";
import { PAYMENT_BUTTON_COMPONENTS } from "../../../../../../constants";
import {
  SrButton,
  SrCurrencyInput,
  SrInput,
  SrTitle,
} from "../../../../../common";
import { validations } from "utils";

const NewButtonForm = ({ setPaymentButtonPage, formValues, setFormValues }) => {
  const classes = useStyles();
  const [showMoreOptions, setShowMoreOptions] = useState(false);

  const handleMoreOptions = (event) => {
    event.preventDefault();
    setShowMoreOptions(!showMoreOptions);
  };

  const initialValues = formValues;

  const validationSchema = Yup.object().shape({
    reference: Yup.string().required(translate("common.requiredField")),
    amount: validations.amount
      .required(translate("common.requiredField"))
      .max(
        1000000,
        translate(
          "ecommerce.botonDePago.createPaymentButton.errors.maxValueMustBe"
        )
      ),
    transactionId: Yup.string(),
    cancelUrl: Yup.string(),
    successUrl: Yup.string(),
  });

  const onSubmit = async (values) => {
    setFormValues(values);
    setPaymentButtonPage?.(PAYMENT_BUTTON_COMPONENTS[2]);
  };

  return (
    <>
      <SrButton
        startIcon={<ArrowBackIcon color="primary" />}
        onClick={() => setPaymentButtonPage?.(PAYMENT_BUTTON_COMPONENTS[0])}
      >
        {translate("common.return")}
      </SrButton>
      <Box maxWidth={720} m="auto">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SrTitle>
              {translate("ecommerce.botonDePago.createPaymentButton.heading")}
            </SrTitle>
          </Grid>
          <Grid item xs={12}>
            <Typography>
              {translate(
                "ecommerce.botonDePago.createPaymentButton.subheading"
              )}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                touched,
                values,
                isSubmitting,
              }) => (
                <Form>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <SrInput
                        variant="outlined"
                        label={translate(
                          "ecommerce.botonDePago.createPaymentButton.form.reference"
                        )}
                        name="reference"
                        type="text"
                        value={values.reference}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.reference && touched.reference)}
                        helperText={touched.reference && errors.reference}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <SrCurrencyInput
                        variant="outlined"
                        label={translate(
                          "ecommerce.botonDePago.createPaymentButton.form.amount"
                        )}
                        name="amount"
                        value={values.amount}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.amount && touched.amount)}
                        helperText={touched.amount && errors.amount}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box textAlign="left">
                        <SrButton
                          className={classes.buttonAction}
                          color="secondary"
                          variant="contained"
                          onClick={handleMoreOptions}
                        >
                          {showMoreOptions
                            ? translate(
                                "ecommerce.botonDePago.createPaymentButton.form.hideMoreOptions"
                              )
                            : translate(
                                "ecommerce.botonDePago.createPaymentButton.form.showMoreOptions"
                              )}
                        </SrButton>
                      </Box>
                    </Grid>
                    <AnimatePresence>
                      {showMoreOptions && (
                        <Grid
                          item
                          container
                          spacing={2}
                          component={motion.div}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                        >
                          <Grid item xs={12} md={6}>
                            <SrInput
                              variant="outlined"
                              label={translate(
                                "ecommerce.botonDePago.createPaymentButton.form.transactionId"
                              )}
                              name="transactionId"
                              type="text"
                              value={values.transactionId}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={Boolean(
                                errors.transactionId && touched.transactionId
                              )}
                              helperText={
                                (touched.transactionId &&
                                  errors.transactionId) ||
                                translate(
                                  "ecommerce.botonDePago.createPaymentButton.form.idHelperText"
                                )
                              }
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <SrInput
                              label="Tiendas"
                              fullWidth
                              select
                              variant="outlined"
                              name="stores"
                              value={values.stores}
                              onChange={handleChange}
                            >
                              <MenuItem value="1">
                                <FormattedMessage id="allStores" />
                              </MenuItem>
                              <MenuItem value="2">
                                {translate(
                                  "ecommerce.botonDePago.createPaymentButton.form.stores.oxxoOnly"
                                )}
                              </MenuItem>
                              <MenuItem value="0">
                                {translate(
                                  "ecommerce.botonDePago.createPaymentButton.form.stores.noStorePayments"
                                )}
                              </MenuItem>
                            </SrInput>
                          </Grid>
                          <Grid item xs={12}>
                            <SrInput
                              variant="outlined"
                              label={translate(
                                "ecommerce.botonDePago.createPaymentButton.form.cancelUrl"
                              )}
                              name="cancelUrl"
                              type="text"
                              value={values.cancelUrl}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={Boolean(
                                errors.cancelUrl && touched.cancelUrl
                              )}
                              helperText={
                                (touched.cancelUrl && errors.cancelUrl) ||
                                translate(
                                  "ecommerce.botonDePago.createPaymentButton.form.cancelUrlHelper"
                                )
                              }
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <SrInput
                              variant="outlined"
                              label={translate(
                                "ecommerce.botonDePago.createPaymentButton.form.successUrl"
                              )}
                              name="successUrl"
                              type="text"
                              value={values.successUrl}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={Boolean(
                                errors.successUrl && touched.successUrl
                              )}
                              helperText={
                                (touched.successUrl && errors.successUrl) ||
                                translate(
                                  "ecommerce.botonDePago.createPaymentButton.form.completeRedirectionHelper"
                                )
                              }
                              fullWidth
                            />
                          </Grid>
                        </Grid>
                      )}
                    </AnimatePresence>
                    <Grid item xs={12}>
                      <Box textAlign="left">
                        <SrButton
                          variant="contained"
                          type="submit"
                          color="primary"
                          disabled={isSubmitting}
                        >
                          {translate(
                            "ecommerce.botonDePago.createPaymentButton.form.createButton"
                          )}
                        </SrButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default NewButtonForm;
